import { useState } from "react";
import {
  useAuth,
  useProperties,
  useCreateProperty,
  useUpdateProperty,
  useDeleteProperty,
} from "@/lib/hooks";
import { Property } from "@/lib/types";
import { Login } from "./Login";
import { AdminNavigation } from "./AdminNavigation";
import { PropertyList } from "./PropertyList";
import { PropertyForm } from "./PropertyForm";
import { ContactList } from "./ContactList";
import { TestimonialManagement } from "./TestimonialManagement";
import { Toaster } from "@/components/ui/sonner";
import { useLanguage } from "@/lib/language";

type View = "list" | "create" | "edit" | "contacts" | "testimonials";

export function AdminPanel() {
  const [currentView, setCurrentView] = useState<View>("list");
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null
  );
  const { dir } = useLanguage();

  const {
    isAuthenticated,
    login,
    googleLogin,
    logout,
    isLoading: authLoading,
    loginError,
  } = useAuth();
  const {
    data: properties = [],
    isLoading: propertiesLoading,
    error: propertiesError,
  } = useProperties();
  const createPropertyMutation = useCreateProperty();
  const updatePropertyMutation = useUpdateProperty();
  const deletePropertyMutation = useDeleteProperty();

  const handleEdit = (property: Property) => {
    setSelectedProperty(property);
    setCurrentView("edit");
  };

  const handleCreate = () => {
    setSelectedProperty(null);
    setCurrentView("create");
  };

  const handleSave = async (
    propertyData: Partial<Property>,
    imageFiles?: File[],
    existingImagePaths?: string[],
    videoFile?: File,
    keepExistingVideo?: boolean
  ) => {
    try {
      if (currentView === "create") {
        await createPropertyMutation.mutateAsync({
          propertyData: propertyData as Omit<
            Property,
            "id" | "createdAt" | "updatedAt"
          >,
          imageFiles,
          videoFile,
        });
      } else if (currentView === "edit" && selectedProperty) {
        await updatePropertyMutation.mutateAsync({
          id: selectedProperty.id,
          data: propertyData,
          imageFiles,
          existingImagePaths,
          videoFile,
          keepExistingVideo,
        });
      }
      setCurrentView("list");
      setSelectedProperty(null);
    } catch (error) {
      console.error("Error saving property:", error);
    }
  };

  const handleDelete = async (propertyId: string) => {
    if (window.confirm("Are you sure you want to delete this property?")) {
      try {
        await deletePropertyMutation.mutateAsync(propertyId);
      } catch (error) {
        console.error("Error deleting property:", error);
      }
    }
  };

  const handleCancel = () => {
    setCurrentView("list");
    setSelectedProperty(null);
  };

  const handleGoogleLogin = (credential: string) => {
    googleLogin({ credential });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Login onGoogleLogin={handleGoogleLogin} isLoading={authLoading} error={loginError} />
    );
  }

  return (
    <div className="min-h-screen bg-background" dir={dir}>
      <AdminNavigation
        currentView={currentView}
        onNavigate={setCurrentView}
        onLogout={logout}
        onCreateNew={handleCreate}
      />

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {currentView === "list" && (
          <PropertyList
            properties={properties}
            loading={propertiesLoading}
            error={propertiesError}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onCreateNew={handleCreate}
          />
        )}

        {currentView === "contacts" && <ContactList />}

        {currentView === "testimonials" && <TestimonialManagement />}

        {(currentView === "create" || currentView === "edit") && (
          <PropertyForm
            property={selectedProperty}
            onSave={handleSave}
            onCancel={handleCancel}
            isLoading={
              createPropertyMutation.isPending ||
              updatePropertyMutation.isPending
            }
          />
        )}
      </main>

      <Toaster />
    </div>
  );
}
